import type { BankProfile } from './validators/epcQr'

/**
 * Per-bank payment behaviour.
 *
 * Two things vary between Belgian banks and neither is documented anywhere
 * reliable, so both are configuration rather than assumptions in code:
 *
 *  1. Whether a Belgian +++structured communication+++ is read out of the
 *     UNSTRUCTURED remittance field (correct per the standard, since it is not
 *     an ISO 11649 RF reference) or the structured one.
 *  2. Whether the app can be launched by URL scheme.
 *
 * Both need testing with a real code before trusting them. `docs/qr-belgium.md`
 * has the test matrix.
 */

export interface Bank {
  id: string
  name: string
  /** Untested guesses are null. A wrong scheme fails silently, which is worse
   *  than no button at all — so we show no button until one is confirmed. */
  urlScheme: string | null
  profile: BankProfile
  note?: string
}

export const BANKS: Bank[] = [
  {
    id: 'belfius',
    name: 'Belfius',
    urlScheme: null,
    profile: { referenceField: 'unstructured', version: '002', requiresBic: false },
  },
  {
    id: 'revolut',
    name: 'Revolut',
    urlScheme: null,
    profile: { referenceField: 'unstructured', version: '002', requiresBic: false },
  },
  {
    id: 'kbc',
    name: 'KBC',
    urlScheme: null,
    profile: { referenceField: 'unstructured', version: '002', requiresBic: false },
  },
  {
    id: 'ing',
    name: 'ING',
    urlScheme: null,
    profile: { referenceField: 'unstructured', version: '002', requiresBic: false },
  },
  {
    id: 'bnp',
    name: 'BNP Paribas Fortis',
    urlScheme: null,
    profile: { referenceField: 'unstructured', version: '002', requiresBic: false },
  },
]

const STORAGE_KEY = 'familie.bank'

export function getPreferredBank(): Bank {
  const id = localStorage.getItem(STORAGE_KEY)
  return BANKS.find((b) => b.id === id) ?? BANKS[0]!
}

export function setPreferredBank(id: string): void {
  localStorage.setItem(STORAGE_KEY, id)
}
